/**
 * An ability's behaviors, instantiated on its first cast instead of when it is
 * built (action-bar plan phase 2). Building then never needs a loaded behavior
 * class, so no import order can leave an ability broken, and a lookup that
 * fails is retried on the next cast.
 *
 * `list` is the array the cast runtimes (windup, toggle) hold; `ensure()` fills
 * it once, in place, before the first cast runs.
 */
import type { AbilityMetadata } from './AbilityRegistry';
import type { AbilityBehavior } from './behaviors/types';
import type { TimelineEntry } from './timeline/AbilityTimeline';
import { getBehaviorClass, resolveBehaviors, type BuilderConfigs } from './BehaviorRegistry';

export interface LazyBehaviors {
  readonly list: AbilityBehavior[];
  ensure(): void;
}

export function lazyBehaviors(
  abilityId: string,
  configs: BuilderConfigs,
  metadata: Partial<AbilityMetadata>,
  timeline: { duration: number | null; entries: TimelineEntry[] },
): LazyBehaviors {
  const list: AbilityBehavior[] = [];
  let ready = false;
  return {
    list,
    ensure() {
      if (ready) return;
      const resolved = resolveBehaviors(abilityId, configs, metadata);
      // A timeline schedules the DSL entries via CastTimelineSystem; normal
      // behaviors still execute at t=0 (backwards compatible).
      if (timeline.duration != null && timeline.entries.length > 0) {
        resolved.push(new (getBehaviorClass('timeline'))(timeline.entries, timeline.duration));
      }
      list.push(...resolved);
      ready = true;
    },
  };
}
